import { Dialog, useMediaQuery } from '@mui/material';
import { V2ThemeProvider } from '@onevision-resources/design-theme';
import { forwardRef, useEffect, useId, useMemo, useRef } from 'react';
import { useModalStore } from 'src/store/modals';
import { useScreenDimensions } from 'src/utils/system';
import { ModalContainerPaper } from './modal-container-paper';
import {
  getModalPaperSx,
  getModalTransitionDurations,
  REDUCED_MOTION_QUERY,
} from './modal-container.styles';
import type { ModalContainerProps } from './modal-container.types';
import { ModalStep } from './modal-step';
import { ModalStepStack } from './modal-step-stack';
import { ModalContainerContext } from './modal-steps-context';

/** The one dialog every portal modal renders through: header, scrolling body, footer, steps. */
export const ModalContainer = forwardRef<HTMLDivElement, ModalContainerProps>(
  (
    {
      open,
      title,
      titleTestId,
      testId,
      size = 'medium',
      draggable = true,
      onClose,
      headerActions,
      actions,
      children,
      ...rest
    },
    ref,
  ) => {
    const titleId = useId();
    const closeBlockedRef = useRef(false);
    const { isMobile } = useScreenDimensions();
    const fullScreen = isMobile && size !== 'small';
    const prefersReducedMotion = useMediaQuery(REDUCED_MOTION_QUERY);
    const transitionDurations = getModalTransitionDurations(prefersReducedMotion);
    const containerValue = useMemo(
      () => ({ titleId, onClose, closeBlockedRef, fullScreen }),
      [titleId, onClose, fullScreen],
    );
    const registerOpenDialog = useModalStore((state) => state.registerOpenDialog);

    useEffect(() => (open ? registerOpenDialog() : undefined), [open, registerOpenDialog]);

    const handleDialogClose = () => {
      if (!closeBlockedRef.current) {
        onClose();
      }
    };

    return (
      <V2ThemeProvider>
        <Dialog
          {...rest}
          ref={ref}
          open={open}
          onClose={handleDialogClose}
          maxWidth={false}
          fullScreen={fullScreen}
          aria-labelledby={titleId}
          transitionDuration={transitionDurations.dialog}
          slotProps={{
            ...rest.slotProps,
            backdrop: { transitionDuration: transitionDurations.backdrop },
            paper: {
              ...(draggable && { component: ModalContainerPaper }),
              sx: getModalPaperSx(size, fullScreen),
            },
          }}
        >
          <ModalContainerContext.Provider value={containerValue}>
            <ModalStepStack
              root={
                title === undefined ? (
                  children
                ) : (
                  <ModalStep
                    title={title}
                    titleTestId={titleTestId}
                    testId={testId}
                    headerActions={headerActions}
                    actions={actions}
                  >
                    {children}
                  </ModalStep>
                )
              }
            />
          </ModalContainerContext.Provider>
        </Dialog>
      </V2ThemeProvider>
    );
  },
);

ModalContainer.displayName = 'ModalContainer';
